import { DOCUMENT } from '@angular/common';
import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Component as BrComponent, Page } from '@bloomreach/spa-sdk';
import {
  AnalyticsService,
  BreadcrumbItem,
  ComponentTheme,
  CustomDimensions,
  NavigationService,
  ResponsiveService,
  SpacingValue,
  StickyNavLink,
  StickyTab,
  StickyTabChange,
  WindowService,
} from '@frk/eds-components';
import { FundCommentaryService } from '@literature/fund-commentary/services/fund-commentary.service';
import { WINDOW } from '@ng-web-apis/common';
import { FundAttributionService } from '@products/fund-attribution/services/fund-attribution.service';
import { FundTitleService } from '@products/fund-header/services/fund-title.service';
import { FundPerformanceComponentConfig } from '@products/fund-performance/fund-performance-component.config';
import { FundPressReleasesDto } from '@products/fund-press-releases/fund-press-releases.service';
import {
  AppStateService,
  AsyncContentManagerService,
  AsyncPageSection,
  DebugService,
  IsLoginUser,
  ProfileService,
  SiteConfigService,
  TabsStickyService,
  WindowScrollService,
} from '@services';
import { ViewModeService } from '@services/view-mode.service';
import { TranslateService } from '@shared/translate/translate.service';
import {
  ConfigurationId,
  FundHeaderLink,
  FundId,
  FundShareClassId,
  FundTitleState,
  ShareClassCode,
} from '@types';
import { Logger } from '@utils/logger';
import cloneDeep from 'lodash/cloneDeep';
import kebabCase from 'lodash/kebabCase';
import { Observable, Subject, combineLatest } from 'rxjs';
import { filter, map, take, takeUntil, tap } from 'rxjs/operators';
import { PRODUCT_PAGE_SECTIONS } from './product-detail-layout.config';
import { ProductDetail, ProductDetailService } from './product-detail.service';
import { ETFTrackingBasketService } from '../../interactive-content/putnam/etf-tracking-basket/services/etf-tracking-basket.service';
import {
  AnalyticsProductData,
  PageViewService,
} from '@services/page-view.service';

const logger = Logger.getLogger('ProductDetailLayoutComponent');

export interface ProductDetailComponent extends BrComponent {
  tabLabelKey: string;
  tabId: string;
}

@Component({
  selector: 'ft-product-detail-layout',
  templateUrl: './product-detail-layout.component.html',
  styleUrls: ['./product-detail-layout.component.scss'],
  // turn off view encapsulation, custom templates
  encapsulation: ViewEncapsulation.None,
})
export class ProductDetailLayoutComponent
  implements OnInit, OnDestroy, AfterViewChecked {
  private unsubscribe$: Subject<void> = new Subject<void>();
  public activeTabId: string;
  public performanceComponents: ProductDetailComponent[] = [];
  public portfolioAllocationsComponents: ProductDetailComponent[] = [];
  public portfolioHoldingsComponents: ProductDetailComponent[] = [];
  public distributionComponents: ProductDetailComponent[] = [];
  public pricingComponents: ProductDetailComponent[] = [];
  public page: Page;
  public sectionsToShow: {
    [sectionName: string]: {
      display: boolean;
      isOddSection: boolean;
      populate: boolean;
      index: number;
    };
  } = {};
  public fundTabs: StickyTab[] = [];
  private brFundTabs: StickyTab[] = [];
  public hasMarketingContent = false;
  public fundId: FundId;
  public simLogo$: Observable<string>;
  public pdBreadcrumbs: BreadcrumbItem[];
  private isHandheld$: Observable<boolean>;
  public canShow = true;
  public stickyFundName: string;
  private populateAll = false; // for Smarsh
  public isLitPreview = false;
  public componentTheme: ComponentTheme;
  public breadcrumbSpacing: SpacingValue;
  public backToTopTitle: string;
  public stickyNavLinks: StickyNavLink[];
  public isEditMode = false;
  public isShareclassLevelRegulatory = false;
  private eTFTrackingBasket: Array<BrComponent> = [];
  constructor(
    private viewModeService: ViewModeService,
    private changeDetectorRef: ChangeDetectorRef,
    private siteConfig: SiteConfigService,
    private productDetailService: ProductDetailService,
    private translateService: TranslateService,
    private responsiveService: ResponsiveService,
    private navService: NavigationService,
    private appStateService: AppStateService,
    private meta: Meta,
    private title: Title,
    @Inject(DOCUMENT) private dom: Document,
    @Inject(WINDOW) readonly windowRef: Window,
    private tabsStickyService: TabsStickyService,
    private scrollService: WindowScrollService,
    private analytics: AnalyticsService,
    private activeRoute: ActivatedRoute,
    private router: Router,
    private debugService: DebugService,
    private fundCommentaryService: FundCommentaryService,
    private fundAttributionService: FundAttributionService,
    private profileService: ProfileService,
    private acm: AsyncContentManagerService,
    private fundTitleService: FundTitleService,
    private eTFTrackingBasketService: ETFTrackingBasketService,
    private windowService: WindowService,
    private pageViewService: PageViewService
  ) {
    this.isEditMode = this.viewModeService.isEditMode();
    // populate everything for Smarsh capture
    this.debugService
      .getExpandAllComponents$()
      ?.pipe(takeUntil(this.unsubscribe$))
      .subscribe((populateAll: boolean) => {
        this.populateAll = populateAll;
      });

    // To expand all section while printing
    this.windowService
      .getBeforePrint$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.populateAll = true;
        this.setSectionsDetail();
        this.changeDetectorRef.detectChanges();
      });
  }

  public ngOnInit(): void {
    this.componentTheme = this.appStateService.getFundHeaderTheme();
    this.acm.registerSections(PRODUCT_PAGE_SECTIONS, true);

    this.isHandheld$ = this.responsiveService.isHandheld$();
    // DCE-2162 use Flag to switch in Old and New Regulatory fund UI
    this.checkShareclassLevelFund();
    const params = this.page?.getComponent()?.getParameters();
    if (params) {
      if (!this.isEditMode) {
        this.canShow = this.checkIfViewable(params);
        if (!this.canShow) {
          return;
        }

        this.fundId = params.fundId;

        const productDetail$: Observable<ProductDetail> = this.productDetailService.getProductDetails$(
          params.fundId,
          params.shareClassCode
        );

        this.isLitPreview = params.pageType === 'lit-preview';

        // Get fund tabs from configuration.
        // First load from configuration
        this.brFundTabs = this.siteConfig
          .getFundTabSet(
            params.fundId,
            params.shareClassCode,
            null, // Passed 'null' intentionally.
            this.isLitPreview
          )
          .tabs.map(
            (tab: string): StickyTab => ({
              id: tab,
              title: this.translateService.instant(
                `products.fund-tab-${tab}`,
                params.fundId
              ),
              childIds: PRODUCT_PAGE_SECTIONS.find(
                (section: AsyncPageSection): boolean => section.id === tab
              )?.childIds,
            })
          );

        this.setFundName(productDetail$);
        this.removeDynamicTabsInitially(); // remove portfolio/attribution/distribution/press-releases
        this.setStickyTabsAndCheckDistributions(productDetail$);
        this.setPageTitleAndMetadata(productDetail$);
        this.setLogo(productDetail$);
        this.setAnalytics(productDetail$);

        this.checkPerformanceComponents(params.fundId);

        this.checkDistributionTab(productDetail$);

        this.checkPricingComponents(params.fundId, params.shareClassCode);
        this.checkETFTrackingBasket(params.fundId, params.shareClassCode);

        // Determine tabs from data for Portfolio Holdings
        this.checkPortfolioComponents(
          params.fundId,
          'portfolio-holdings',
          'portfolioHoldingsComponents'
        );

        // Determine tabs from data for Portfolio Allocations
        this.checkPortfolioComponents(
          params.fundId,
          'portfolio-breakdowns',
          'portfolioAllocationsComponents'
        );

        // Do we have any marketing Content?
        this.checkMarketingContent(productDetail$);

        // TODO: delete after new Comentary code tested
        // this.showHideCommentarySection({
        //   fundId: params?.fundId,
        //   shareClassCode: params?.shareClassCode,
        // });

        this.handlePortfolioComponentTabDisplay(params?.fundId);
        this.handleAttributionComponentTabDisplay(
          params?.fundId,
          params?.shareClassCode
        );

        // WDE-11: Hide Price and Performance sections for not loggedin users in VIO

        productDetail$
          .pipe(
            takeUntil(this.unsubscribe$),
            // TODO: side-effects like this are generally discouraged
            tap(() => {
              this.productDetailService.setVIPNonLoggedInStatus(false);
            }),
            filter(
              (productDetail) =>
                productDetail.productType === ConfigurationId.VIP
            )
          )
          .subscribe(() => {
            if (!this.performanceComponents.length) {
              this.hideSection('performance');
            }
            if (!this.pricingComponents.length) {
              this.hideSection('pricing');
            }
            if (!this.profileService.isLoggedIn()) {
              this.productDetailService.setVIPNonLoggedInStatus(true);
            }
          });

        // Listen for active tab scrolling change and update highlight
        this.tabsStickyService
          .getActiveTabId$()
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe((id: string): void => {
            logger.debug('getActiveTabId$', id);
            this.activeTabId = id;
            this.changeDetectorRef.detectChanges();
          });
      } else {
        // runs only when isEditMode
        this.fundTabs = this.siteConfig
          .getFundTabSet(params.fundId, params.shareClassCode, null) // Passed 'null' intentionally.
          .tabs.map((tab) => ({
            id: tab,
            title: this.translateService.instant(`products.fund-tab-${tab}`),
          }));
        this.setSectionsDetail();
      }
    }

    this.backToTopTitle = this.translateService.instant(
      'common.sticky-tab-back-to-top'
    );

    this.fundTitleService.fundTitleState
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((fundTitleState: FundTitleState) => {
        this.stickyNavLinks = fundTitleState?.data?.fundHeaderLinks?.reduce(
          (resultLinks: StickyNavLink[], link: FundHeaderLink) => {
            if (link.displayInStickyNavigation) {
              return resultLinks.concat({
                displayText: link.displayText,
                redirectLink: link.redirectLink,
                iconType: link.iconType,
                tooltipContent: link.tooltipContent,
                isNewWindow: link.openInNewWindow,
              });
            }
            return resultLinks;
          },
          []
        );
      });
  }

  ngAfterViewChecked(): void {
    const urlParams = new URLSearchParams(this.windowRef.location.search);
    const portfolioHoldingsParam = urlParams.get('portfolio-holdings');
    if (portfolioHoldingsParam) {
      this.scrollService.scrollToAnchor('portfolio-holdings');
    }
  }

  /**
   *
   * @param fundId the idof fund for the product detail page
   * here for this particular id the etf tracking basket is enabled from document is verified
   * if it is valid then only it is inserted in portfolio holdings component to get a tab for etf tracking baskets
   */
  private checkETFTrackingBasket(fundId: string, shareClassCode: string): void {
    const component = this.page
      ?.getComponent('portfolio-holdings')
      ?.getChildren()
      ?.filter((comp) => comp.getName() === 'etf-tracking-baskets')[0];
    const config = this.page
      ?.getContent(component?.getModels()?.document)
      ?.getData();
    if (
      config?.fundSpecificEtfTracking.filter((fund) => fund.fundId === fundId)
        .length
    ) {
      this.eTFTrackingBasketService.setEtfTrackingFunds(shareClassCode, fundId);
      this.eTFTrackingBasket.push(component);
    }
  }
  private checkShareclassLevelFund() {
    this.page
      ?.getComponent('overview')
      ?.getChildren()
      .map((elm) => {
        if (elm.getModels().config?.componentType === 'Fund Information') {
          this.isShareclassLevelRegulatory = elm.getModels().config?.isShareclassLevelRegulatory;
        }
      });
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public initialize(page: Page): void {
    this.page = page;
  }

  private hideSection(sectionId: string): void {
    const tabIndex = this.fundTabs.findIndex((tab) => tab.id === sectionId);

    if (tabIndex !== -1) {
      this.fundTabs.splice(tabIndex, 1);
      this.fundTabs = this.fundTabs.slice(0);
      this.setSectionsDetail();
      this.tabsStickyService.setStickyTabs(this.fundTabs);
    }
  }

  // Commentary will be done through BR content instead of Literature API
  // TODO: delete this code once new commentary code is confirmed to work as expected
  // private showHideCommentarySection(params: ProductDetailConfiguration): void {
  //   // Then check if we have commentary for this fund
  //   if (this.sectionsToShow.commentary) {
  //     // Need to check we have data first so first remove
  //     this.profileService
  //       .getUserProfile$()
  //       ?.pipe(
  //         takeUntil(this.unsubscribe$),
  //         filter((profile: IUserProfile): boolean => {
  //           if (profile.isLoggedIn) {
  //             return !!profile?.profileInfo?.userId;
  //           } else {
  //             return true;
  //           }
  //         }),
  //         switchMap((profileData) => {
  //           if (
  //             profileData.isLoggedIn &&
  //             UserRole[profileData?.profileInfo?.role] ===
  //               SH.toLocaleLowerCase()
  //           ) {
  //             return of(false);
  //           } else {
  //             return this.fundCommentaryService.showCommentarySection$(params);
  //           }
  //         })
  //       )
  //       .subscribe((data: boolean): void => {
  //         this.sectionsToShow.commentary.display = data;

  //         if (!this.sectionsToShow.commentary.display) {
  //           const tabIndex = this.fundTabs.findIndex(
  //             (tab) => tab.id === 'commentary'
  //           );

  //           if (tabIndex !== -1) {
  //             this.fundTabs.splice(tabIndex, 1);
  //             this.fundTabs = this.fundTabs.slice(0);
  //             this.setSectionsDetail();
  //             this.tabsStickyService.setStickyTabs(this.fundTabs);
  //           }
  //         }
  //       });
  //   }
  // }

  private checkMarketingContent(
    productDetail$: Observable<ProductDetail>
  ): void {
    productDetail$.pipe(take(1)).subscribe((productDetail) => {
      this.hasMarketingContent = this.productDetailService.hasMarketingContent(
        this.page,
        this.page?.getComponent('marketing-content').getChildren(),
        productDetail.productType
      );
    });
  }

  private checkIfViewable(params: Record<string, any>): boolean {
    const id = `${params.fundId}-${params.shareClassCode}` as FundShareClassId;
    const path = this.dom.location.pathname;
    if (
      this.siteConfig.literature.litDetailUrl &&
      path.includes(this.siteConfig.literature.litDetailUrl)
    ) {
      this.router.navigateByUrl(path, { skipLocationChange: true }).then(() => {
        window.location.reload();
      });
      return false;
    } else if (this.isRedirectValid(id, path, params)) {
      // inactive share class or wrong page for fund so redirect
      let redirectUrl = this.siteConfig.product.ppss?.redirectToPpssUrl;
      redirectUrl = redirectUrl
        ? this.appStateService.getSpaBaseUrl() + redirectUrl
        : this.appStateService.getHomePageUrl();
      this.navService.navigateByUrl(redirectUrl, '_self');
      return false;
    }

    return true;
  }

  private isRedirectValid(
    id: FundShareClassId,
    path: string,
    params: Record<string, any>
  ): boolean {
    /**
     * redirect condition:
     *    not US
     *    and,
     *      not active and not softclosed
     *      or,
     *      generated path contains fund id
     *      but not, shareClassCode and doesn't match full url
     */

    let fundStatus: string;
    let fundCountry: string;
    if (!this.siteConfig.isActiveShareClass(id)) {
      fundStatus = 'inactive';
    }
    if (this.siteConfig.isSoftClosedShareClass(id)) {
      fundStatus = 'softclosed';
    }
    if (this.siteConfig.isSoftLaunchShareClass(id)) {
      fundStatus = 'softlaunch';
    }
    if (this.siteConfig.isSiteInternational()) {
      fundCountry = 'Intl';
    } else {
      fundCountry = 'US';
    }
    switch (fundStatus) {
      case 'softlaunch':
        // On softlaunch redirect to PPSS
        return true;
      case 'inactive':
        if (fundCountry === 'US' || fundCountry === 'Intl') {
          // On inactive and US or international redirect to PPSS
          return true;
        }
        break;
      case 'softclosed':
        if (fundCountry === 'US' || this.siteConfig.isAustralia()) {
          // On softclosed and US return false
          return false;
        }
        if (fundCountry === 'Intl') {
          // On softclosed and Intl redirect to PPSS
          return true;
        }
        break;
      default:
        //  if no case matches return false
        return false;
    }

    if (path.indexOf(params.fundId) !== -1) {
      if (
        path.indexOf(
          this.siteConfig.getFundLink(params.fundId, params.shareClassCode)
        ) === -1 &&
        path.indexOf(
          this.siteConfig.getFundLink(
            params.fundId,
            params.shareClassCode,
            params.fundName,
            params.identifier,
            true
          )
        ) === -1
      ) {
        return true;
      }
    }
    return false;
  }

  public handlePortfolioTabSelection(): void {
    this.productDetailService.handlePortfolioTabSelection();
  }

  public handlePricingTabSelection(): void {
    this.productDetailService.handlePricingTabSelection();
  }

  private setPageTitleAndMetadata(
    productDetail$: Observable<ProductDetail>
  ): void {
    productDetail$.pipe(take(1)).subscribe((productDetail) => {
      const title = this.getPageTitle(productDetail);
      const identifierSuffix = productDetail.identifier
        ? ` - ${productDetail.identifier}`
        : '';
      const description = `${this.translateService.instant(
        'products.product-page-description'
      )} ${productDetail.fundName}${identifierSuffix}.`;
      this.title.setTitle(title);
      this.meta.updateTag({ name: 'description', content: description });
      this.meta.updateTag({
        property: 'og:title',
        content: title,
      });
      this.meta.updateTag({
        property: 'og:description',
        content: description,
      });
      this.setCanonical(productDetail.url);
      this.setBreadcrumbs(productDetail.url);
    });
  }

  private getPageTitle(productDetail: ProductDetail): string {
    const shareClassSuffix = productDetail.shareClassName
      ? ` - ${productDetail.shareClassName}`
      : '';
    const identifierSuffix = productDetail.identifier
      ? ` - ${productDetail.identifier}`
      : '';
    // NGC-14397: Missing " Currency" next to the fund name display in ribbon view and breadcrumb.
    const currencySuffix =
      productDetail.shareClassCurrency &&
      this.siteConfig.isCanada() &&
      productDetail.productType === 'Mutual Funds'
        ? ` - ${productDetail.shareClassCurrency}`
        : '';
    return `${productDetail.fundName}${shareClassSuffix}${identifierSuffix}${currencySuffix}`;
  }

  private setBreadcrumbs(url: string): void {
    const models = this.page.getComponent('breadcrumbs')?.getModels();
    if (models?.breadcrumbs) {
      this.pdBreadcrumbs = models.breadcrumbs
        .map((bc) => ({
          content: bc.title,
          href: bc.url,
        }))
        .concat([
          {
            content: this.title.getTitle(),
            url,
          },
        ]);
    }

    // Set breadcrumb margin if none exist.
    this.breadcrumbSpacing = this.pdBreadcrumbs?.length > 0 ? 'none' : 'md';
  }

  private setCanonical(url: string): void {
    const value = url
      ? this.windowRef.location.origin + url
      : this.windowRef.location.href;
    let link: HTMLLinkElement =
      this.dom.querySelector(`link[rel='canonical']`) || null;
    if (!link) {
      link = this.dom.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.dom.head.appendChild(link);
    }
    link.setAttribute('href', value);
  }

  private setLogo(productDetail$: Observable<ProductDetail>) {
    this.simLogo$ = combineLatest([productDetail$, this.isHandheld$]).pipe(
      map(([productDetails, isHandHeld]) => {
        let image = this.translateService.instant(
          `products.logo-sim-${kebabCase(
            productDetails.investmentManager?.toLowerCase() // tlc needed so ClearBridge doesn't get hyphenated
          )}`
        );
        // FIXME find better way to handle smaller images
        if (isHandHeld) {
          image = image.replace(
            'width="200" height="100"',
            'width="128" height="64"'
          );
        }
        return image.startsWith('[') ? '' : image;
      })
    );
  }

  private checkPortfolioComponents(
    fundId: FundId,
    container: string,
    memberToUpdate: string
  ): void {
    const configuredPortfolioComponents: BrComponent[] = this.page
      ?.getComponent(container)
      .getChildren()
      ?.map(
        (cmp: ProductDetailComponent): ProductDetailComponent => {
          cmp.tabLabelKey = this.getPortfolioTabHeading(cmp);
          cmp.tabId = cmp.getName();
          return cmp;
        }
      );

    combineLatest([
      this.productDetailService.getPortfolioComponents$(
        fundId,
        configuredPortfolioComponents
      ),
      this.debugService.isPortfolioDebug$(),
    ])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        ([portfolioComponents, isDebug]: [BrComponent[], boolean]): void => {
          /**
           * after confirming from checkETFTrackingBasket for this particular fund they are added
           *  in portfolio holdings tabs
           */
          if (
            memberToUpdate === 'portfolioHoldingsComponents' &&
            this.eTFTrackingBasket.length
          ) {
            portfolioComponents.push(this.eTFTrackingBasket[0]);
          }

          this[memberToUpdate] = isDebug
            ? configuredPortfolioComponents
            : portfolioComponents;
        }
      );
  }

  /**
   * Handle the display of portfolio tab.
   * @param fundId current product detail page fund id
   */
  private handlePortfolioComponentTabDisplay(fundId: FundId): void {
    // Check if, 'portfolio' id exists in br config response.
    const portfolioIndex: number = this.brFundTabs.findIndex(
      (tab) => tab.id === 'portfolio'
    );

    // If yes, then proceed with getting status for the same.
    if (portfolioIndex !== -1) {
      // Declare portfolio containers to check.
      const portfolioContainers: string[] = [
        'portfolio-statistics',
        'portfolio-holdings',
        'portfolio-breakdowns',
      ];

      // Fetch portfolio BR components for declared containers.
      const configuredPortfolioComponents: BrComponent[] = [];
      portfolioContainers.forEach((container) => {
        this.page
          ?.getComponent(container)
          .getChildren()
          .forEach((component) => {
            configuredPortfolioComponents.push(component);
          });
      });

      // Invoke component status for all configured portfolio components.
      this.productDetailService
        .getPortfolioComponentStatus(fundId, configuredPortfolioComponents)
        .then((flag: boolean) => {
          if (flag) {
            const perfFundTabIndex: number = this.fundTabs.findIndex(
              (tab) => tab.id === 'performance'
            );
            const attributionFundTabIndex: number = this.fundTabs.findIndex(
              (tab) => tab.id === 'attribution'
            );
            const portfolio = this.brFundTabs.find(
              (tab) => tab.id === 'portfolio'
            );

            // ***** ASSUMPTION *****
            // Portfolio Hierarchy: Overview > Performance > Attribution > Portfolio.
            let expectedPortfolioIndex: number;
            if (attributionFundTabIndex !== -1) {
              expectedPortfolioIndex = attributionFundTabIndex + 1;
            } else {
              // In case both Attribution & Performance not present, then default to 1, i.e., after Overview.
              expectedPortfolioIndex =
                perfFundTabIndex !== -1 ? perfFundTabIndex + 1 : 1;
            }

            this.fundTabs.splice(expectedPortfolioIndex, 0, portfolio);
            this.fundTabs = this.fundTabs.slice(0);

            this.setSectionsDetail();
            this.tabsStickyService.setStickyTabs(this.fundTabs);
          }
        })
        .catch((error) =>
          logger.error(`Error in getting portfolio component status: ${error}`)
        );
    }
  }

  /**
   * Handle the display of attribution tab.
   * @param fundId current product detail page fund id
   * @param shareClassCode current product detail page fund share class code
   */
  private handleAttributionComponentTabDisplay(
    fundId: FundId,
    shareClassCode: ShareClassCode
  ): void {
    const attrFundTabIndex: number = this.brFundTabs.findIndex(
      (tab) => tab.id === 'attribution'
    );
    if (attrFundTabIndex !== -1) {
      this.fundAttributionService
        .getAttributionData({
          fundId,
          shareClassCode,
        })
        ?.pipe(takeUntil(this.unsubscribe$))
        .subscribe((attributionData) => {
          if (
            attributionData[0].country.length ||
            attributionData[0].sector.length ||
            attributionData[1].country.length ||
            attributionData[1].sector.length
          ) {
            if (attrFundTabIndex !== -1) {
              const perfFundTabIndex: number = this.fundTabs.findIndex(
                (tab) => tab.id === 'performance'
              );
              const attributionFundTab: StickyTab = this.brFundTabs[
                attrFundTabIndex
              ];

              this.fundTabs.splice(perfFundTabIndex + 1, 0, attributionFundTab);
              this.fundTabs = this.fundTabs.slice(0);
            }

            this.setSectionsDetail();
            this.tabsStickyService.setStickyTabs(this.fundTabs);
          }
        });
    }
  }

  private checkDistributionTab(productDetail$: Observable<ProductDetail>) {
    productDetail$
      .pipe(take(1), takeUntil(this.unsubscribe$))
      .subscribe((productDetail) => {
        this.distributionComponents = this.page
          ?.getComponent('distributions')
          .getChildren()
          ?.map(
            (cmp: ProductDetailComponent): ProductDetailComponent => {
              switch (cmp.getName()) {
                case 'fund-press-releases':
                  const pressReleasesDto: FundPressReleasesDto = cmp.getModels();
                  if (pressReleasesDto.releases.releases.length > 0) {
                    return this.getProductDetailComponent(cmp);
                  }
                  break;
                case 'distribution':
                  if (
                    !this.isDistributionDisabled(productDetail, this.fundId)
                  ) {
                    return this.getProductDetailComponent(cmp);
                  }
                  break;
                case 'fund-tax':
                  return this.getProductDetailComponent(cmp);
              }
            }
          )
          .filter((data) => data !== undefined);

        if (!this.distributionComponents.length) {
          this.hideSection('distributions');
        }
      });
  }
  private isDistributionDisabled(
    distributions: ProductDetail,
    fundId: FundId
  ): boolean {
    return (
      !distributions.hasDistributions ||
      (this.siteConfig.hideDistributionPerShare(fundId) &&
        this.siteConfig.isSiteInternational())
    );
  }

  private getProductDetailComponent(
    cmp: ProductDetailComponent
  ): ProductDetailComponent {
    cmp.tabLabelKey = this.getDistributionTabHeading(cmp);
    cmp.tabId = cmp.getName();
    return cmp;
  }

  private checkPricingComponents(
    fundId: FundId,
    shareClassCode: ShareClassCode
  ): void {
    const configuredPricingComponents: BrComponent[] = this.page
      ?.getComponent('pricing')
      ?.getChildren();
    if (configuredPricingComponents && configuredPricingComponents.length > 0) {
      this.productDetailService
        .getPricingComponents$(
          fundId,
          shareClassCode,
          configuredPricingComponents
        )
        .pipe(takeUntil(this.unsubscribe$)) // We expect the subscription 2 times or more.
        .subscribe((components: BrComponent[]) => {
          if (components[0] !== undefined) {
            this.pricingComponents = components?.map(
              (cmp: ProductDetailComponent): ProductDetailComponent => {
                cmp.tabLabelKey = this.getPricingTabHeading(cmp);
                cmp.tabId = cmp.getName();
                return cmp;
              }
            );
          }
        });
    }
  }

  // this checks BR data for the fund-press-release component, and whether it contains any releases
  private hasPressReleases(): boolean {
    logger.debug(
      'fund-press-releases',
      this.page
        ?.getComponent('press-releases')
        ?.getComponent('fund-press-releases')
    );
    const pressReleasesDto: FundPressReleasesDto = this.page
      ?.getComponent('press-releases')
      ?.getComponent('fund-press-releases')
      ?.getModels<FundPressReleasesDto>();
    return pressReleasesDto?.releases?.releases.length > 0;
  }

  // this checks BR data for the commentary >> fund-content component, and whether it contains any documents
  private hasCommentary(): boolean {
    logger.debug(
      'commentary >> fund-content',
      this.page?.getComponent('commentary')?.getComponent('fund-content')
    );

    // TODO: fund-content component needs to define a data type for model
    const docs: DocumentData[] = this.page
      ?.getComponent('commentary')
      ?.getComponent('fund-content')
      ?.getModels()?.documents;
    return docs?.length > 0;
  }

  private checkPerformanceComponents(fundId: FundId): void {
    const configuredPerformanceComponents: BrComponent[] = this.page
      ?.getComponent('performance')
      ?.getChildren();
    if (configuredPerformanceComponents?.length) {
      this.productDetailService
        .getPerformanceComponents$(fundId, configuredPerformanceComponents)
        .pipe(take(1))
        .subscribe((components: BrComponent[]) => {
          this.performanceComponents = components?.map(
            (cmp: ProductDetailComponent): ProductDetailComponent => {
              cmp.tabLabelKey = this.getPerformanceTabHeading(cmp);
              cmp.tabId = FundPerformanceComponentConfig.getConfig(
                cmp.getModels()?.config?.componentType
              ).fragmentId;
              return cmp;
            }
          );
        });
    }
  }

  private removeDynamicTabsInitially(): void {
    const initialFundTabs = cloneDeep(this.brFundTabs);

    const portfolioIndex = initialFundTabs.findIndex(
      (tab: StickyTab) => tab.id === 'portfolio'
    );
    if (portfolioIndex !== -1) {
      initialFundTabs.splice(portfolioIndex, 1);
    }

    const attributionIndex = initialFundTabs.findIndex(
      (tab: StickyTab) => tab.id === 'attribution'
    );
    if (attributionIndex !== -1) {
      initialFundTabs.splice(attributionIndex, 1);
    }

    const distributionIndex = initialFundTabs.findIndex(
      (tab: StickyTab) => tab.id === 'distributions'
    );
    if (distributionIndex !== -1) {
      initialFundTabs.splice(distributionIndex, 1);
    }

    // remove press-releases tab if no press releases data
    const pressReleasesIndex = initialFundTabs.findIndex(
      (tab: StickyTab) => tab.id === 'press-releases'
    );
    if (pressReleasesIndex !== -1 && !this.hasPressReleases()) {
      initialFundTabs.splice(pressReleasesIndex, 1);
    }

    // remove commentary tab if no commentarey BR data
    const commentaryIndex = initialFundTabs.findIndex(
      (tab: StickyTab) => tab.id === 'commentary'
    );
    if (commentaryIndex !== -1 && !this.hasCommentary()) {
      initialFundTabs.splice(commentaryIndex, 1);
    }

    this.fundTabs = initialFundTabs;
    this.setSectionsDetail();
  }

  private setStickyTabsAndCheckDistributions(
    productDetail$: Observable<ProductDetail>
  ): void {
    const tabIndex = this.brFundTabs.findIndex(
      (tab) => tab.id === 'distributions'
    );
    if (tabIndex !== -1) {
      productDetail$.pipe(take(1)).subscribe((productDetail) => {
        if (productDetail.hasDistributions || productDetail.hasTax) {
          const pricingTabIndex = this.fundTabs.findIndex(
            (tab) => tab.id === 'pricing'
          );

          const distTab: StickyTab = this.brFundTabs[tabIndex];

          this.fundTabs.splice(pricingTabIndex, 0, distTab);
          this.fundTabs = this.fundTabs.slice(0);
        }

        this.setSectionsDetail();
        this.tabsStickyService.setStickyTabs(this.fundTabs);
      });
    } else {
      this.tabsStickyService.setStickyTabs(this.fundTabs);
    }
  }

  private setFundName(productDetail$: Observable<ProductDetail>): void {
    productDetail$.pipe(take(1)).subscribe((productDetail) => {
      this.stickyFundName = this.getPageTitle(productDetail);
    });
  }

  private populateSection(id: string): void {
    logger.debug(
      'Populating',
      id,
      'Already populated:',
      this.sectionsToShow[id]?.populate
    );
    // only run this if section isn't populated yet
    // avoids race conditions
    if (this.sectionsToShow[id] && !this.sectionsToShow[id].populate) {
      // set this section as populated
      this.sectionsToShow[id].populate = true;

      // look for a following section that isn't populated yet
      const nextSectionId = this.getNextUnpopulatedSection(id);
      logger.debug('nextSectionId', nextSectionId);
      if (nextSectionId) {
        // if unpopulated section, populate after a short delay
        setTimeout(() => {
          logger.debug('populate nextSectionId after delay', nextSectionId);
          this.populateSection(nextSectionId);
        }, 300);
      }
    }
  }

  private getNextUnpopulatedSection(currentId: string): string {
    logger.debug('getNextUnpopulatedSection', currentId, this.fundTabs);
    const currentIndex: number = this.fundTabs.findIndex(
      (tab: StickyTab): boolean => tab.id === currentId
    );
    if (currentIndex === -1) {
      return null;
    }
    const nextTab: StickyTab = this.fundTabs.find((tab: StickyTab): boolean => {
      const section = this.sectionsToShow[tab.id];
      // find first tab after current tab that is not populated
      return section.index > currentIndex && !section.populate;
    });
    return nextTab?.id || null;
  }

  private setSectionsDetail() {
    const sections = {};

    this.fundTabs.forEach((tab, index) => {
      sections[tab.id] = {
        display: true,
        isOddSection: index % 2 !== 0,
        populate: this.sectionsToShow[tab.id]?.populate || this.populateAll,
        index,
      };
    });
    this.sectionsToShow = sections;
    logger.debug('sectionsToShow', this.sectionsToShow);
  }

  public onTabChange(tabEvent: StickyTabChange): void {
    logger.debug('onTabChange()', tabEvent);
    if (tabEvent.type !== 'manual') {
      // this will suppress ScrollSpy for non-scroll events
      this.tabsStickyService.tabClicked(tabEvent.tab);
    }
    this.populateSection(tabEvent.tab.id);
  }

  public onStickyTabsHeightChange(height: number): void {
    logger.debug('onStickyTabsHeightChange()', height);
    this.scrollService.stickyTabsResize('product-detail-sticky-tabs', height);
  }

  private getPerformanceTabHeading(component: BrComponent): string {
    return `products.performance-tab-${kebabCase(
      component.getModels()?.config?.componentType
    )}`;
  }

  private getPortfolioTabHeading(component: BrComponent): string {
    return `products.portfolio-${component.getName()}-tab`;
  }

  private getDistributionTabHeading(component: BrComponent): string {
    return `products.distributions-${component.getName()}-tab`;
  }

  private getPricingTabHeading(component: BrComponent): string {
    return `products.pricing-${component.getName()}-tab`;
  }

  private setAnalytics(productDetail$: Observable<ProductDetail>): void {
    combineLatest([productDetail$, this.activeRoute.fragment])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([productDetail, fragment]) => {
        if (!fragment) {
          fragment = this.fundTabs[0]?.title;
        }
        this.pageViewService.setProductAnalytics(
          productDetail as AnalyticsProductData,
          this.getPageTitle(productDetail),
          fragment,
          this.fundId,
          this.page.getComponent()?.getName()
        );
      });
  }
}
